import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import { getMovieById, getRelatedMovies, getCategoryByKey, formatViews } from '@/utils/movie';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function DetailPage() {
  const { movieId } = useParams<{ movieId: string }>();
  const movie = getMovieById(movieId || '');
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  const related = useMemo(() => (movie ? getRelatedMovies(movie, 10) : []), [movie]);
  const category = movie ? getCategoryByKey(movie.type) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [movieId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  usePageMeta({
    title: movie ? `${movie.title}(${movie.year}) - 高清电影详情 - 高清电影` : '影片详情 - 高清电影',
    description: movie
      ? `${movie.title}(${movie.year})高清电影详情：${movie.region}${category?.name}影片，由${movie.director}执导，${movie.actors.join('、')}主演。${movie.synopsis}`
      : '查看高清电影影片详情、剧情简介与相关推荐。',
    keywords: movie
      ? `${movie.title},${movie.title}高清,${category?.name}电影,${movie.director},${movie.actors.join(',')}`
      : '高清电影,影片详情',
  });

  if (!movie) {
    return (
      <PageLayout>
        <div className="w-full px-4 py-16 md:px-8">
          <EmptyState
            icon="ri-file-unknow-line"
            title="该影片不存在或已下架"
            description="你访问的影片可能已经下架，去首页发现更多精彩电影吧。"
            actionText="返回首页"
          />
        </div>
      </PageLayout>
    );
  }

  const favorited = isFavorite(movie.id);

  const handleFavorite = () => {
    const active = toggleFavorite(movie.id);
    setToast(active ? '收藏成功，已保存到本地浏览器' : '已取消收藏');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制到剪贴板');
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setToast('链接已复制到剪贴板');
    }
  };

  return (
    <PageLayout>
      <div className="w-full px-4 py-6 md:px-8 md:py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="cursor-pointer hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-base text-foreground-300"></i>
          <Link to={`/category/${movie.type}`} className="cursor-pointer hover:text-primary-600">
            {category?.name}电影
          </Link>
          <i className="ri-arrow-right-s-line text-base text-foreground-300"></i>
          <span className="text-foreground-800">{movie.title}</span>
        </nav>

        {/* Hero: split layout */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
          <div className="mx-auto w-full max-w-[280px] lg:mx-0 lg:max-w-none">
            <div className="overflow-hidden rounded-lg bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title}高清海报`}
                title={`${movie.title}高清电影海报`}
                className="aspect-[2/3] w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex flex-col gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-bold transition-colors cursor-pointer ${
                  favorited
                    ? 'bg-accent-500 text-white hover:bg-accent-600'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <i className={`${favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-lg`}></i>
                {favorited ? '已收藏' : '收藏影片'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-5 py-3 text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-share-forward-line text-lg"></i>
                复制链接分享
              </button>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${movie.type}`}
                className="rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700 transition-colors hover:bg-primary-200 cursor-pointer"
              >
                {category?.name}
              </Link>
              <span className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
                {movie.region}
              </span>
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-700">
                <i className="ri-star-fill text-xs"></i>
                {movie.rating.toFixed(1)}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              {movie.title}
              <span className="ml-2 text-lg font-medium text-foreground-400">({movie.year})</span>
            </h1>

            {/* Meta */}
            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-background-200/70 bg-background-50 p-5 sm:grid-cols-3">
              <div>
                <p className="text-xs text-foreground-400">上映年份</p>
                <p className="mt-1 text-sm font-bold text-foreground-900">{movie.year} 年</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400">影片类型</p>
                <p className="mt-1 text-sm font-bold text-foreground-900">{category?.name}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400">制片地区</p>
                <p className="mt-1 text-sm font-bold text-foreground-900">{movie.region}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400">导演</p>
                <p className="mt-1 text-sm font-bold text-foreground-900">{movie.director}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400">片长</p>
                <p className="mt-1 text-sm font-bold text-foreground-900">{movie.duration} 分钟</p>
              </div>
              <div>
                <p className="text-xs text-foreground-400">浏览热度</p>
                <p className="mt-1 text-sm font-bold text-foreground-900">{formatViews(movie.views)}</p>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <p className="text-xs text-foreground-400">主演</p>
                <p className="mt-1 text-sm font-bold text-foreground-900">{movie.actors.join(' / ')}</p>
              </div>
            </div>

            {/* Synopsis */}
            <div className="mt-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-book-open-line text-lg text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.synopsisFull}</p>
            </div>

            {/* Tags */}
            <div className="mt-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-price-tag-3-line text-lg text-primary-500"></i>
                影片标签
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {movie.tags.map((t) => (
                  <span key={t} className="rounded-md bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <p className="mt-6 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
              <i className="ri-information-line mr-1"></i>
              本页面为演示内容，不提供真实播放与下载服务。影片信息均为虚构演示数据。
            </p>
          </div>
        </div>

        {/* Related */}
        <section className="mt-14">
          <h2 className="mb-6 flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 text-white">
              <i className="ri-movie-2-line text-lg"></i>
            </span>
            相关推荐
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 md:gap-4">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-full bg-foreground-900 px-5 py-3 text-sm font-medium text-background-50 shadow-lg">
            <i className="ri-checkbox-circle-line text-lg text-accent-400"></i>
            {toast}
          </div>
        </div>
      )}
    </PageLayout>
  );
}